import {
  detectCTAVisualFileType,
  type FileTypeDetectionResult,
} from "@jugl-web/domain-resources/chat-bot/utils/detectCTAVisualFileType";
import { FC, ReactNode, useEffect, useState } from "react";

export interface CustomCTAVisualPreviewProps {
  /** Resolved URL of the custom CTA visual to display. */
  src: string;
  /** Content rendered while the file type is being detected or when the media type is unsupported. */
  fallback?: ReactNode;
  className?: string;
}

/**
 * Renders a user-uploaded CTA visual (image or video).
 * The component first probes the media type of `src`, then renders the
 * appropriate HTML element once the file type is known.
 */
export const CustomCTAVisualPreview: FC<CustomCTAVisualPreviewProps> = ({
  src,
  fallback = null,
  className,
}) => {
  const [fileTypeDetectionResult, setFileTypeDetectionResult] =
    useState<FileTypeDetectionResult | null>(null);

  // Detect file type to know which element to render
  useEffect(() => {
    let canceled = false;

    detectCTAVisualFileType(src)
      .then((result) => {
        if (!canceled) {
          setFileTypeDetectionResult(result);
        }
      })
      .catch(() => {
        if (!canceled) {
          setFileTypeDetectionResult({
            fileType: "unknown",
            isValid: false,
            isImage: false,
            isVideo: false,
          });
        }
      });

    return () => {
      canceled = true;
    };
  }, [src]);

  if (!fileTypeDetectionResult) {
    return fallback;
  }

  const mediaClassName = className ?? "h-full w-full object-cover";
  const { isVideo, isImage } = fileTypeDetectionResult;

  if (isVideo) {
    return (
      <video
        src={src}
        className={mediaClassName}
        muted
        playsInline
        loop
        autoPlay
      />
    );
  }

  if (isImage) {
    return <img src={src} alt="" className={mediaClassName} />;
  }

  return fallback;
};
